import { useEffect, useState } from "react";
import { ArrowRight, PhoneCall, PlayCircle } from "lucide-react";
import api from "../api";
import Seo from "../components/Seo";
import { useLang, Link } from "../i18n";
import VehicleCard from "../components/VehicleCard";
import Reveal from "../components/Reveal";

const VIDEOS = [
  { src: "/videos/machinery-1.mp4", poster: "/videos/machinery-1.jpg", t: "mach.v1t", d: "mach.v1d", id: "machinery-video-1" },
  { src: "/videos/machinery-2.mp4", poster: "/videos/machinery-2.jpg", t: "mach.v2t", d: "mach.v2d", id: "machinery-video-2" },
];

export default function Machinery() {
  const { t } = useLang();
  const [stock, setStock] = useState([]);

  useEffect(() => {
    api.get("/vehicles", { params: { category: "forklifts" } })
      .then((r) => setStock(r.data.filter((v) => v.status !== "sold")))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="machinery-page" className="pt-32 pb-24">
      <Seo title={t("seo.machTitle")} description={t("seo.machDesc")} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono-num text-xs tracking-[0.35em] text-[#4499F7] uppercase mb-4">KATACO WORLDWIDE</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50">{t("mach.title")}</h1>
          <p className="mt-5 text-slate-400 max-w-2xl leading-relaxed">{t("mach.sub")}</p>
        </Reveal>

        <div className="mt-16" data-testid="machinery-videos">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-50 flex items-center gap-3">
              <PlayCircle size={26} className="text-[#4499F7]" />
              {t("mach.videosTitle")}
            </h2>
            <p className="mt-3 text-slate-400 max-w-xl">{t("mach.videosSub")}</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {VIDEOS.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.1}>
                <div className="bg-[#12141D] border border-white/10 rounded-3xl overflow-hidden" data-testid={v.id}>
                  <div className="relative aspect-video bg-black">
                    <video
                      src={v.src}
                      poster={v.poster}
                      controls
                      preload="metadata"
                      playsInline
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-semibold text-lg text-slate-50">{t(v.t)}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t(v.d)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {stock.length > 0 && (
          <div className="mt-20" data-testid="machinery-stock">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-50">{t("mach.stockTitle")}</h2>
              <p className="mt-3 text-slate-400 max-w-xl">{t("mach.stockSub")}</p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {stock.map((v, i) => (
                <VehicleCard key={v.id} vehicle={v} index={i} />
              ))}
            </div>
          </div>
        )}

        <Reveal>
          <div className="mt-20 rounded-3xl border border-[rgba(68,153,247,0.25)] bg-[#12141D] p-8 lg:p-10 text-center spotlight-card overflow-hidden" data-testid="machinery-cta">
            <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto">{t("mach.ctaText")}</p>
            <div className="mt-7 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="machinery-enquire-btn"
                className="group flex items-center gap-2 bg-[#4499F7] text-[#0A0B10] font-semibold text-sm rounded-full px-7 py-3.5 hover:bg-[#6FB1FA] transition-colors"
              >
                {t("form.send")}
                <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <a
                href="tel:+40723250260"
                data-testid="machinery-call-btn"
                className="flex items-center gap-2 border border-white/20 text-slate-100 font-semibold text-sm rounded-full px-7 py-3.5 hover:border-[rgba(68,153,247,0.6)] hover:text-[#4499F7] transition-colors"
              >
                <PhoneCall size={15} />
                +40 723 250 260
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
